import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Activity,
  Building2,
  CalendarCheck,
  FileText,
  HeartPulse,
  RefreshCw,
  Users,
} from 'lucide-react';
import { TimelineChart } from '@/components/charts/TimelineChart';
import { METRIC_META } from '@/components/charts/metricMeta';
import { Spinner } from '@/components/base/Spinner';
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue';
import { useHospitalStats } from '@/lib/hooks/useHospitalStats';
import { useAuthStore } from '@/lib/stores/authStore';
import {
  parseDateParam,
  todayIst,
  type DateRange,
  type TimelineMetric,
} from '@/lib/utils/hospitalStatsCalc';

interface KpiCardProps {
  title: string;
  value: number | string;
  detail?: React.ReactNode;
  icon: React.ReactNode;
}

function KpiCard({ title, value, detail, icon }: KpiCardProps) {
  return (
    <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="font-hanken text-base font-medium text-gray-800">{title}</span>
        <span className="text-primary">{icon}</span>
      </div>
      <div className="mt-3 font-hanken text-3xl font-semibold text-pranik-brand">{value}</div>
      {detail && <div className="mt-2 text-xs text-gray-500">{detail}</div>}
    </div>
  );
}

// Chart shows only Appointments and Consultations (same as hmis-dashboard);
// PA and Vitals appear as cards only.
const ALL_METRICS: TimelineMetric[] = ['appointments', 'consultations'];

/** How long the dates must stay unchanged before the stats are requested. */
const RANGE_DEBOUNCE_MS = 500;

export function HospitalDashboardView() {
  const hospitalName = useAuthStore((state) => state.user?.projectName);

  // Date range and chart tab live in the URL (?from=&to=&metric=) so a refresh keeps them.
  const [searchParams, setSearchParams] = useSearchParams();
  const today = todayIst();
  const from = parseDateParam(searchParams.get('from'), today) ?? today;
  const parsedTo = parseDateParam(searchParams.get('to'), today);
  const to = parsedTo && parsedTo >= from ? parsedTo : from;
  const metricParam = searchParams.get('metric');
  const selectedMetric: TimelineMetric = ALL_METRICS.includes(metricParam as TimelineMetric)
    ? (metricParam as TimelineMetric)
    : 'appointments';

  const range = useMemo<DateRange>(() => ({ from, to }), [from, to]);

  // The date boxes update at once, but the request waits until the dates stop changing,
  // so stepping through months does not fire (and cancel) a request per step.
  const fetchRange = useDebouncedValue(range, RANGE_DEBOUNCE_MS);

  const { data, isLoading, isError, error, isFetching, refetch } = useHospitalStats(fetchRange);

  const updateParams = (next: { from?: string; to?: string; metric?: TimelineMetric }) => {
    setSearchParams(
      {
        from: next.from ?? from,
        to: next.to ?? to,
        metric: next.metric ?? selectedMetric,
      },
      { replace: true }
    );
  };

  const setFrom = (value: string) => updateParams({ from: value, to: to < value ? value : to });
  const setTo = (value: string) => updateParams({ from: from > value ? value : from, to: value });
  const setSelectedMetric = (metric: TimelineMetric) => updateParams({ metric });

  // Follows the data on screen (not the date boxes) so the chart caption matches the chart.
  const singleDay = fetchRange.from === fetchRange.to;

  return (
    <div className="py-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-hanken text-xl font-medium text-gray-700">Hospital Dashboard</h1>
          {hospitalName && (
            <span className="mt-2 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-sm text-primary shadow-sm">
              <Building2 className="h-4 w-4" />
              {hospitalName}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 text-base font-medium text-gray-800">
          <label className="flex items-center gap-2">
            From
            <input
              type="date"
              value={range.from}
              max={todayIst()}
              onChange={(e) => e.target.value && setFrom(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-base text-gray-900"
            />
          </label>
          <label className="flex items-center gap-2">
            To
            <input
              type="date"
              value={range.to}
              max={todayIst()}
              onChange={(e) => e.target.value && setTo(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-base text-gray-900"
            />
          </label>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            aria-label="Refresh statistics"
            title="Refresh"
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 transition-colors hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center py-20" role="status" aria-label="Loading statistics">
          <Spinner size="lg" />
        </div>
      )}

      {isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Could not load hospital statistics:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <KpiCard
              title="Unique patients"
              value={data.uniquePatients}
              detail={
                <div className="text-sm font-medium text-gray-700">
                  <span>Completed - {data.completedPatients ?? 0}</span>
                </div>
              }
              icon={<Users className="h-5 w-5" />}
            />
            <KpiCard
              title="Appointments"
              value={data.appointments}
              icon={<CalendarCheck className="h-5 w-5" />}
            />
            <KpiCard title="PA" value={data.pa} icon={<FileText className="h-5 w-5" />} />
            <KpiCard title="Vitals" value={data.vitals} icon={<HeartPulse className="h-5 w-5" />} />
            <KpiCard
              title="Consultations"
              value={data.consultations.total}
              detail={
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm font-medium text-gray-700">
                  <span>Completed - {data.consultations.completed}</span>
                  <span>On hold - {data.consultations.onHold}</span>
                  <span>Not saved - {data.consultations.notSaved}</span>
                </div>
              }
              icon={<Activity className="h-5 w-5" />}
            />
          </div>

          <div className="mt-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-hanken text-lg font-medium text-gray-800">Activity timeline</h2>
                <p className="text-xs text-gray-500">
                  {singleDay ? 'Hourly distribution (IST)' : 'Daily totals (IST)'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {ALL_METRICS.map((metric) => {
                  const active = selectedMetric === metric;
                  return (
                    <button
                      key={metric}
                      type="button"
                      onClick={() => setSelectedMetric(metric)}
                      aria-pressed={active}
                      className={`rounded-lg px-3 py-1 text-sm transition-colors ${
                        active ? 'bg-primary-100 text-pranik-brand' : 'bg-gray-50 text-gray-500'
                      }`}
                    >
                      {METRIC_META[metric].label}
                    </button>
                  );
                })}
              </div>
            </div>
            <TimelineChart
              data={data.timeline}
              metrics={[selectedMetric]}
              height="clamp(320px, calc(100vh - 430px), 720px)"
            />
          </div>
        </>
      )}
    </div>
  );
}
